/**
 * Thin wrapper around `@monaco-editor/react` + the bundled `monaco-editor`
 * npm package for Flint's SQL / Template editors. Owns theme, layout, and
 * the read-only fallback. Everything outside this file (toolbar, footer,
 * container chrome) continues to come from `CodeEditor` in preview-pane.tsx.
 */
import Editor, { loader } from "@monaco-editor/react";
import * as monaco from "monaco-editor";

// Use the bundled monaco-editor package — no CDN fetch.
loader.config({ monaco });

const FLINT_THEME = "flint-dark";

// Register the theme at module load time so the very first <Editor />
// that mounts already has `flint-dark` available. Registering it in an
// `onMount` callback ran AFTER Monaco's first paint, which caused the
// first-ever SQL tab open to fall back to the default light theme
// until a full tab remount forced a re-render.
monaco.editor.defineTheme(FLINT_THEME, {
  base: "vs-dark",
  inherit: true,
  rules: [
    { token: "", foreground: "e2e8f0" },
    { token: "keyword.sql", foreground: "38bdf8", fontStyle: "bold" },
    { token: "string.sql", foreground: "86efac" },
    { token: "comment.sql", foreground: "64748b", fontStyle: "italic" },
    { token: "number.sql", foreground: "fbbf24" },
    { token: "operator.sql", foreground: "cbd5e1" },
    { token: "tag.html", foreground: "38bdf8" },
    { token: "attribute.name.html", foreground: "fbbf24" },
    { token: "attribute.value.html", foreground: "86efac" },
  ],
  colors: {
    "editor.background": "#0f172a",
    "editor.foreground": "#e2e8f0",
    "editorLineNumber.foreground": "#475569",
    "editorLineNumber.activeForeground": "#94a3b8",
    "editor.selectionBackground": "#0369a144",
    "editorCursor.foreground": "#38bdf8",
    "editor.lineHighlightBackground": "#1e293b",
    "editorWidget.background": "#1e293b",
    "editorWidget.border": "#334155",
    "editorGutter.background": "#0f172a",
  },
});

interface MonacoCodeEditorProps {
  value: string;
  onChange?: (next: string) => void;
  language: "sql" | "html" | "json";
  ariaLabel: string;
}

export function MonacoCodeEditor({
  value,
  onChange,
  language,
  ariaLabel,
}: MonacoCodeEditorProps) {
  const readOnly = !onChange;

  return (
    <Editor
      value={value}
      language={language}
      theme={FLINT_THEME}
      {...(onChange ? { onChange: (v: string | undefined) => onChange(v ?? "") } : {})}
      loading={<EditorSkeleton />}
      options={{
        readOnly,
        minimap: { enabled: false },
        padding: { top: 8, bottom: 8 },
        fontSize: 12,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
        lineNumbers: "on",
        renderLineHighlight: readOnly ? "none" : "line",
        scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
        smoothScrolling: true,
        automaticLayout: true,
        wordWrap: language === "html" ? "on" : "off",
        tabSize: 2,
        cursorBlinking: "smooth",
        contextmenu: !readOnly,
        ariaLabel,
      }}
    />
  );
}

function EditorSkeleton() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-[#0f172a] text-xs text-slate-500">
      Loading editor…
    </div>
  );
}
